import { useLayoutEffect, useRef, type ReactNode } from 'react'

import EmptyState, { type EmptyStateProps } from '@/components/ui/EmptyState'
import type { EmptyKind, StateSize } from '@/components/ui/empty-state-variants'
import { SkeletonLayer } from '@/components/ui/Skeleton'
import { useSkeletonTiming } from '@/components/ui/use-skeleton-timing'
import { useOnline } from '@/hooks/useOnline'
import { cn } from '@/lib/utils'
import { lastUpdatedMeta, resolveViewState, savedAtMeta, type ViewSource } from '@/lib/view-state'

/** The slice of a `useQuery` result this reads. A `UseQueryResult<T>` passes as it is. */
export interface QueryStateSource<T> extends ViewSource<T> {
  isFetching: boolean
  dataUpdatedAt: number
  refetch: () => Promise<unknown>
}

/** Everything EmptyState takes, less what QueryState decides for itself. */
type StateOverrides = Omit<
  EmptyStateProps,
  'kind' | 'size' | 'framed' | 'onRetry' | 'retrying' | 'className'
>

interface QueryStateProps<T> {
  query: QueryStateSource<T>
  /** The data arrived with nothing in it: `(rows) => rows.length === 0`. */
  isEmpty?: ((data: T) => boolean) | undefined
  /**
   * Required, and shaped like the loaded layout: every loading frame in the
   * design is a skeleton of its content, never a spinner in a box.
   */
  skeleton: ReactNode
  /** Read out while loading, since every Skeleton is aria-hidden. */
  loadingLabel?: string | undefined
  size?: StateSize | undefined
  framed?: boolean | undefined
  /** Copy for the empty state. `kind: 'no-results'` for a search or filter that matched nothing. */
  empty?: (StateOverrides & { kind?: EmptyKind | undefined }) | undefined
  failed?: StateOverrides | undefined
  offline?: StateOverrides | undefined
  /** Defaults to `query.refetch()`. Market screens pass `useManualRefresh([query]).refresh`. */
  onRetry?: (() => void) | undefined
  /** Spacing and min-height, applied whichever state is showing. */
  className?: string | undefined
  /** The loaded view. `data` is the query's data, never `undefined`. */
  children: (data: T) => ReactNode
}

/**
 * One query, every state it can be in: its skeleton, failed, offline, empty,
 * then the content — in the order `resolveViewState` sets, so data already on
 * screen is never swapped for an error block, and an offline first load says
 * offline instead of loading forever.
 *
 * Focus: the member who pressed "Try again" keeps their place. A retry that
 * fails again leaves the same button focused (it is never disabled). One
 * that works unmounts it — and a removed button drops focus to <body>, where
 * the next Tab starts from the top of the page — so focus is handed to this
 * block instead, whose content now stands where the button was.
 */
export default function QueryState<T>({
  query,
  isEmpty,
  skeleton,
  loadingLabel = 'Loading…',
  size = 'section',
  framed,
  empty,
  failed,
  offline,
  onRetry,
  className,
  children,
}: QueryStateProps<T>) {
  const online = useOnline()
  const resolved = resolveViewState(query, { isEmpty, online })
  // What is on screen. The skeleton is laid out at once but held back from
  // painting for its first 80ms, by the class on the root below, and
  // whatever replaces a painted one crosses it there — content, failed or
  // empty alike, since the root outlives every state. See
  // use-skeleton-timing.ts.
  const timing = useSkeletonTiming(resolved === 'loading')
  const view = timing.showSkeleton ? 'loading' : resolved

  const rootRef = useRef<HTMLDivElement>(null)
  const shownView = useRef(view)
  const focusInside = useRef(false)

  useLayoutEffect(() => {
    if (shownView.current === view) return
    shownView.current = view
    if (!focusInside.current) return
    const active = document.activeElement
    if (active === null || active === document.body) rootRef.current?.focus()
  }, [view])

  const retry =
    onRetry ??
    (() => {
      void query.refetch()
    })

  let body: ReactNode = null
  if (view === 'loading') {
    body = (
      <span role="status" className="sr-only">
        {loadingLabel}
      </span>
    )
  } else if (view === 'failed' || view === 'offline') {
    // One element for both kinds, so a retry that comes back offline (or the
    // reverse) updates the block in place and the focused button survives.
    body = (
      <EmptyState
        kind={view}
        size={size}
        framed={framed}
        onRetry={retry}
        retrying={query.isFetching}
        meta={
          view === 'failed'
            ? lastUpdatedMeta(query.dataUpdatedAt)
            : savedAtMeta(query.dataUpdatedAt)
        }
        {...(view === 'failed' ? failed : offline)}
      />
    )
  } else if (view === 'empty') {
    body = <EmptyState size={size} framed={framed} {...empty} />
  } else if (query.data !== undefined) {
    body = children(query.data)
  }

  return (
    <div
      ref={rootRef}
      tabIndex={-1}
      aria-busy={view === 'loading' || undefined}
      onFocus={() => {
        focusInside.current = true
      }}
      onBlur={(event) => {
        // Only a move to somewhere else counts as leaving. Focus that went
        // nowhere — its element was removed, or the window lost focus — is
        // exactly the case the hand-off above repairs.
        const next = event.relatedTarget
        if (next instanceof Node && !event.currentTarget.contains(next)) focusInside.current = false
      }}
      className={cn('relative outline-none', timing.className, className)}
    >
      {body}
      {/* After the body, in a slot of its own, so it is the same element from
          the load's first frame to the end of its fade-out. */}
      {timing.showSkeleton || timing.skeletonLeaving ? (
        <SkeletonLayer leaving={!timing.showSkeleton}>{skeleton}</SkeletonLayer>
      ) : null}
    </div>
  )
}
